<template>
  <MainLayout :initialDrawerExpanded="false" contentClass="pa-md-4" :has-sidebar="false">
    <template #header>
      <div class="overline">
        <PageBack />
      </div>
    </template>

    <template #content>
      <v-container fluid class="pa-0 pa-md-4">
        <v-row>
          <v-col cols="12" md="10" offset-md="1">

            <div v-if="loading" class="d-flex justify-center py-12">
              <v-progress-circular indeterminate color="primary" />
            </div>

            <v-alert v-else-if="error" type="error" class="mb-4">{{ error }}</v-alert>

            <template v-else-if="plan">
              <!-- Header -->
              <div class="d-flex align-center flex-wrap mb-5" style="gap: 12px">
                <v-avatar color="primary lighten-4" size="48">
                  <v-icon color="primary">mdi-warehouse</v-icon>
                </v-avatar>
                <div>
                  <div class="subtitle-1 font-weight-medium">{{ plan.name }}</div>
                  <div class="caption grey--text">{{ plan.owner_label }}</div>
                </div>
                <v-chip small :color="statusColor(plan.status)" dark>{{ statusLabel(plan.status) }}</v-chip>
                <v-spacer />
                <v-btn small text color="error" @click="remove">
                  <v-icon left small>mdi-delete</v-icon>
                  Supprimer
                </v-btn>
              </div>

              <!-- Items -->
              <v-card elevation="0" class="mb-4 rounded-lg" outlined>
                <v-card-title class="subtitle-1 font-weight-medium pb-1">
                  <v-icon left small>mdi-format-list-bulleted</v-icon>
                  Articles
                  <v-spacer />
                  <span class="caption grey--text">{{ itemsCount }} résultat(s)</span>
                </v-card-title>
                <v-card-text>
                  <div class="d-flex flex-wrap" style="gap: 12px">
                    <v-text-field
                      v-model="itemsQuery"
                      label="Rechercher (désignation, code, EAN)"
                      prepend-inner-icon="mdi-magnify"
                      clearable
                      dense
                      outlined
                      hide-details
                      style="max-width: 360px"
                      @input="debouncedLoadItems"
                    />
                    <v-select
                      v-model="itemsStatus"
                      :items="itemStatusChoices"
                      dense
                      outlined
                      hide-details
                      style="max-width: 220px"
                      @change="loadItems"
                    />
                  </div>
                  <v-data-table
                    :headers="visibleItemHeaders"
                    :items="items"
                    :loading="itemsLoading"
                    :items-per-page="25"
                    dense
                    class="elevation-0 mt-2"
                  >
                    <template v-slot:[`item.image`]="{ item }">
                      <v-img
                        v-if="imageSrc(item)"
                        :src="imageSrc(item)"
                        width="40"
                        height="40"
                        contain
                        class="my-1"
                      />
                    </template>
                    <template v-slot:[`item.is_stocked`]="{ item }">
                      <v-chip x-small :color="item.is_stocked ? 'success' : 'info'" dark>
                        {{ item.is_stocked ? 'Stocké' : 'Commandable' }}
                      </v-chip>
                      <v-chip v-if="!item.is_active" x-small color="grey" dark class="ml-1">Inactif</v-chip>
                    </template>
                    <template v-slot:[`item.attributes`]="{ item }">
                      <span class="caption grey--text">{{ formatAttributes(item.attributes) }}</span>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>

              <v-row align="start">
                <!-- Configuration -->
                <v-col cols="12" md="6">
                  <v-card elevation="0" class="mb-4 rounded-lg" outlined>
                    <v-card-title class="subtitle-1 font-weight-medium pb-1">
                      <v-icon left small>mdi-cog-outline</v-icon>
                      Configuration
                    </v-card-title>
                    <v-card-text>
                      <v-text-field v-model="form.name" label="Nom" dense outlined />
                      <v-textarea v-model="form.description" label="Description" rows="2" auto-grow dense outlined />
                      <v-select v-model="form.scope" :items="scopeChoices" label="Propriétaire" dense outlined @change="form.ownerId = null" />
                      <v-autocomplete
                        v-model="form.ownerId"
                        :items="ownerChoices"
                        item-text="label"
                        item-value="id"
                        :label="scopeLabel"
                        dense
                        outlined
                      />
                      <v-autocomplete
                        v-model="form.sourceFile"
                        :items="meta.files"
                        item-text="name"
                        item-value="id"
                        label="Fichier source (Excel / CSV)"
                        clearable
                        dense
                        outlined
                      />
                      <v-select
                        v-model="form.metiers"
                        :items="meta.metiers"
                        item-text="label"
                        item-value="slug"
                        label="Métiers servis"
                        hint="Vide = plan générique, valable pour tous les métiers."
                        persistent-hint
                        multiple
                        chips
                        small-chips
                        deletable-chips
                        dense
                        outlined
                        class="mb-3"
                      />
                      <v-switch v-model="form.isActive" label="Plan actif" dense hide-details class="mt-0" />
                    </v-card-text>
                    <v-card-actions>
                      <v-spacer />
                      <v-btn small color="primary" elevation="0" :loading="saving" @click="saveConfig">Enregistrer</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-col>

                <!-- Import -->
                <v-col cols="12" md="6">
                  <v-card elevation="0" class="mb-4 rounded-lg" outlined>
                    <v-card-title class="subtitle-1 font-weight-medium pb-1">
                      <v-icon left small>mdi-database-import-outline</v-icon>
                      Import
                    </v-card-title>
                    <v-card-text style="display: grid; gap: 8px">
                      <div class="d-flex" style="gap: 24px">
                        <div>
                          <div class="caption grey--text">Version</div>
                          <div class="body-2">{{ plan.version }}</div>
                        </div>
                        <div>
                          <div class="caption grey--text">Dernier import</div>
                          <div class="body-2">{{ formatDate(plan.imported_at) }}</div>
                        </div>
                        <div>
                          <div class="caption grey--text">Articles actifs</div>
                          <div class="body-2">{{ plan.item_count }} ({{ plan.stocked_count }} stockés)</div>
                        </div>
                      </div>
                      <v-alert v-if="report.failure" type="error" dense text class="mb-0">{{ report.failure }}</v-alert>
                      <div v-if="report.total_rows !== undefined" class="caption">
                        {{ report.total_rows }} lignes lues · {{ report.imported }} importées
                        ({{ report.created }} créées, {{ report.updated }} mises à jour, {{ report.deactivated }} désactivées)
                        · {{ report.not_stocked }} non stockées
                        · {{ report.error_count }} erreurs · {{ report.warning_count }} avertissements
                      </div>
                      <v-expansion-panels v-if="(report.errors || []).length || (report.warnings || []).length" flat>
                        <v-expansion-panel>
                          <v-expansion-panel-header class="caption px-0">Détail des erreurs et avertissements</v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <div v-for="(e, i) in report.errors" :key="`e${i}`" class="caption error--text">Ligne {{ e.row }} : {{ e.message }}</div>
                            <div v-for="(w, i) in report.warnings" :key="`w${i}`" class="caption orange--text">Ligne {{ w.row }} : {{ w.message }}</div>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </v-card-text>
                    <v-card-actions>
                      <span class="caption grey--text">Suivi détaillé dans l'onglet Tâches.</span>
                      <v-spacer />
                      <v-btn
                        small
                        color="primary"
                        elevation="0"
                        :loading="importing || plan.status === 'importing'"
                        :disabled="!plan.source_file"
                        @click="runImport"
                      >
                        <v-icon left small>mdi-play</v-icon>
                        Importer
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Images -->
              <v-card elevation="0" class="mb-4 rounded-lg" outlined>
                <v-card-title class="subtitle-1 font-weight-medium pb-1">
                  <v-icon left small>mdi-image-multiple-outline</v-icon>
                  Images
                </v-card-title>
                <v-card-text style="display: grid; gap: 8px">
                  <div class="d-flex" style="gap: 24px">
                    <div>
                      <div class="caption grey--text">URLs d'images trouvées</div>
                      <div class="body-2">{{ plan.image_url_count }} / {{ plan.item_count }}</div>
                    </div>
                    <div>
                      <div class="caption grey--text">Fichiers téléchargés</div>
                      <div class="body-2">{{ plan.local_image_count }} / {{ plan.item_count }}</div>
                    </div>
                  </div>
                  <div class="caption grey--text">
                    La recherche d'URLs est volontairement lente (requêtes espacées pour éviter le blocage) :
                    elle ne traite que les articles sans image et peut être relancée. Suivi dans l'onglet Tâches.
                  </div>
                  <v-switch v-model="imagesOverwrite" label="Refaire aussi les articles déjà traités" dense hide-details class="mt-0" />
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    small
                    color="primary"
                    elevation="0"
                    :loading="imageTask === 'urls'"
                    :disabled="!!imageTask"
                    @click="runImageTask('urls')"
                  >
                    <v-icon left small>mdi-magnify</v-icon>
                    Chercher les URLs d'images
                  </v-btn>
                  <v-btn
                    small
                    color="primary"
                    elevation="0"
                    :loading="imageTask === 'files'"
                    :disabled="!!imageTask || !plan.image_url_count"
                    @click="runImageTask('files')"
                  >
                    <v-icon left small>mdi-download</v-icon>
                    Télécharger les images
                  </v-btn>
                </v-card-actions>
              </v-card>

              <!-- Column mapping -->
              <v-card elevation="0" class="mb-4 rounded-lg" outlined>
                <v-card-title class="subtitle-1 font-weight-medium pb-1">
                  <v-icon left small>mdi-table-column</v-icon>
                  Mapping des colonnes
                  <v-spacer />
                  <v-menu offset-y>
                    <template #activator="{ on, attrs }">
                      <v-btn small text v-bind="attrs" v-on="on">
                        Appliquer un preset
                        <v-icon right small>mdi-chevron-down</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense>
                      <v-list-item v-for="name in presetNames" :key="name" @click="applyPreset(name)">
                        <v-list-item-title>{{ name }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-card-title>
                <v-card-text>
                  <v-textarea
                    v-model="mappingText"
                    :error-messages="mappingError ? [mappingError] : []"
                    rows="12"
                    auto-grow
                    outlined
                    class="mapping-editor"
                  />
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn small text :loading="testing" :disabled="!plan.source_file" @click="testMapping">
                    <v-icon left small>mdi-flask-outline</v-icon>
                    Tester le mapping
                  </v-btn>
                  <v-btn small color="primary" elevation="0" :loading="savingMapping" @click="saveMapping">
                    Enregistrer le mapping
                  </v-btn>
                </v-card-actions>

                <template v-if="preview">
                  <v-divider />
                  <v-card-text>
                    <div class="caption grey--text mb-2">
                      Colonnes du fichier : {{ preview.headers.join(', ') }}
                    </div>
                    <div v-for="(e, i) in preview.errors" :key="`pe${i}`" class="caption error--text">Ligne {{ e.row }} : {{ e.message }}</div>
                    <div v-for="(w, i) in preview.warnings" :key="`pw${i}`" class="caption orange--text">Ligne {{ w.row }} : {{ w.message }}</div>
                    <v-simple-table dense class="mt-2">
                      <template #default>
                        <thead>
                          <tr>
                            <th>Code</th>
                            <th>Désignation</th>
                            <th>Unité</th>
                            <th>Fournisseur</th>
                            <th>EAN</th>
                            <th>Stocké</th>
                            <th>Attributs</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="row in preview.rows" :key="row.external_id">
                            <td class="caption">{{ row.external_id }}</td>
                            <td class="caption">{{ row.designation }}</td>
                            <td class="caption">{{ row.unit }}</td>
                            <td class="caption">{{ row.supplier_name }}</td>
                            <td class="caption">{{ row.barcode }}</td>
                            <td class="caption">{{ row.is_stocked ? 'Oui' : 'Non' }}</td>
                            <td class="caption grey--text">{{ formatAttributes(row.attributes) }}</td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-card-text>
                </template>
              </v-card>
            </template>
          </v-col>
        </v-row>
      </v-container>
    </template>
  </MainLayout>
</template>

<script>
import PageBack from '@/components/ui/PageBack.vue'
import MainLayout from '@/layout/MainLayout.vue'
import { resolveMediaUrl } from '@/services/mediaUrl'
import { STOCK_PLAN_STATUS, SCOPE_CHOICES, apiErrorMessage } from '@/components/admin/stockPlan'
import {
  fetchAdminStockPlan,
  fetchAdminStockPlanMeta,
  updateAdminStockPlan,
  deleteAdminStockPlan,
  importAdminStockPlan,
  fetchAdminStockPlanImageUrls,
  downloadAdminStockPlanImages,
  fetchAdminTasks,
  testAdminStockPlanMapping,
  fetchAdminStockPlanItems,
} from '@/services/adminApi'

const IMPORT_POLL_MS = 3000
const IMPORT_POLL_MAX = 100
const IMAGE_POLL_MS = 4000
const IMAGE_POLL_MAX = 900 // ~1 h: the URL search is slow on purpose
const TASK_DONE = ['SUCCESS', 'FAILURE', 'REVOKED']

export default {
  name: 'AdminStockPlanDetailView',
  components: { PageBack, MainLayout },

  data() {
    return {
      loading: false,
      error: null,
      plan: null,
      meta: { presets: {}, owners: { users: [], teams: [], organisations: [] }, files: [] },
      form: { name: '', description: '', scope: 'organisation', ownerId: null, sourceFile: null, isActive: true, metiers: [] },
      scopeChoices: SCOPE_CHOICES,
      saving: false,
      importing: false,
      pollTimer: null,
      imageTask: null, // 'urls' | 'files' while a Celery task runs
      imagesOverwrite: false,
      imageTimer: null,
      importBaseline: null,
      mappingText: '',
      mappingError: null,
      savingMapping: false,
      testing: false,
      preview: null,
      items: [],
      itemsCount: 0,
      itemsLoading: false,
      itemsQuery: '',
      itemsStatus: 'active',
      itemsTimer: null,
      itemStatusChoices: [
        { text: 'Actifs', value: 'active' },
        { text: 'Stockés', value: 'stocked' },
        { text: 'Commandables (non stockés)', value: 'not_stocked' },
        { text: 'Inactifs (retirés)', value: 'inactive' },
        { text: 'Tous', value: 'all' },
      ],
      itemHeaders: [
        { text: '', value: 'image', sortable: false, width: 56 },
        { text: 'Code', value: 'external_id' },
        { text: 'Désignation', value: 'designation' },
        { text: 'Unité', value: 'unit' },
        { text: 'Fournisseur', value: 'supplier_name' },
        { text: 'EAN', value: 'barcode' },
        { text: 'Statut', value: 'is_stocked' },
        { text: 'Attributs', value: 'attributes', sortable: false },
      ],
    }
  },

  computed: {
    visibleItemHeaders() {
      const show = this.$store.getters['settings/showProductImages']
      return this.itemHeaders.filter(h => h.value !== 'image' || show)
    },
    planId() {
      return this.$route.params.id
    },
    report() {
      return (this.plan && this.plan.last_import_report) || {}
    },
    presetNames() {
      return Object.keys(this.meta.presets)
    },
    ownerChoices() {
      const key = { organisation: 'organisations', team: 'teams', user: 'users' }[this.form.scope]
      return this.meta.owners[key] || []
    },
    scopeLabel() {
      return (SCOPE_CHOICES.find(c => c.value === this.form.scope) || {}).text
    },
  },

  mounted() {
    this.load()
  },

  beforeDestroy() {
    clearTimeout(this.pollTimer)
    clearTimeout(this.itemsTimer)
    clearTimeout(this.imageTimer)
  },

  methods: {
    async load() {
      this.loading = true
      this.error = null
      try {
        const [plan, meta] = await Promise.all([fetchAdminStockPlan(this.planId), fetchAdminStockPlanMeta()])
        this.meta = meta
        this.setPlan(plan)
        this.loadItems()
        if (plan.status === 'importing') this.pollImport(0)
      } catch (e) {
        this.error = apiErrorMessage(e)
      } finally {
        this.loading = false
      }
    },
    setPlan(plan) {
      this.plan = plan
      this.form = {
        name: plan.name,
        description: plan.description,
        scope: plan.scope,
        ownerId: plan[plan.scope],
        sourceFile: plan.source_file ? plan.source_file.id : null,
        isActive: plan.is_active,
        metiers: plan.metiers || [],
      }
      this.mappingText = JSON.stringify(plan.column_mapping, null, 2)
      this.mappingError = null
    },
    notify(type, message) {
      this.$store.commit('chat/SHOW_NOTIFICATION', { type, message })
    },
    async saveConfig() {
      this.saving = true
      try {
        const plan = await updateAdminStockPlan(this.planId, {
          name: this.form.name,
          description: this.form.description,
          [this.form.scope]: this.form.ownerId,
          source_file: this.form.sourceFile,
          is_active: this.form.isActive,
          metiers: this.form.metiers,
        })
        this.setPlan(plan)
        this.notify('success', 'Plan de stock enregistré.')
      } catch (e) {
        this.notify('error', apiErrorMessage(e))
      } finally {
        this.saving = false
      }
    },
    parseMapping() {
      try {
        this.mappingError = null
        return JSON.parse(this.mappingText)
      } catch (e) {
        this.mappingError = `JSON invalide : ${e.message}`
        return undefined
      }
    },
    applyPreset(name) {
      this.mappingText = JSON.stringify(this.meta.presets[name], null, 2)
      this.mappingError = null
    },
    async saveMapping() {
      const mapping = this.parseMapping()
      if (mapping === undefined) return
      this.savingMapping = true
      try {
        this.setPlan(await updateAdminStockPlan(this.planId, { column_mapping: mapping }))
        this.notify('success', 'Mapping enregistré.')
      } catch (e) {
        this.mappingError = apiErrorMessage(e)
      } finally {
        this.savingMapping = false
      }
    },
    async testMapping() {
      const mapping = this.parseMapping()
      if (mapping === undefined) return
      this.testing = true
      this.preview = null
      try {
        this.preview = await testAdminStockPlanMapping(this.planId, { column_mapping: mapping, limit: 20 })
      } catch (e) {
        this.mappingError = apiErrorMessage(e)
      } finally {
        this.testing = false
      }
    },
    async runImport() {
      this.importing = true
      try {
        this.importBaseline = this.plan.updated_at
        await importAdminStockPlan(this.planId)
        this.notify('info', 'Import lancé.')
        this.pollImport(0)
      } catch (e) {
        this.notify('error', apiErrorMessage(e))
        this.importing = false
      }
    },
    // Poll the plan until the Celery import leaves the "importing" state.
    pollImport(attempt) {
      clearTimeout(this.pollTimer)
      this.pollTimer = setTimeout(async () => {
        try {
          const plan = await fetchAdminStockPlan(this.planId)
          this.plan = plan
          // Until the worker picks the task up, the plan is unchanged.
          const notStarted = this.importing && plan.updated_at === this.importBaseline
          const pending = plan.status === 'importing' || notStarted
          if (pending && attempt < IMPORT_POLL_MAX) {
            this.pollImport(attempt + 1)
            return
          }
          this.setPlan(plan)
          this.importing = false
          this.loadItems()
          if (plan.status === 'ready') this.notify('success', `Import terminé (version ${plan.version}).`)
          if (plan.status === 'failed') this.notify('error', 'Import en échec : voir le rapport.')
        } catch (e) {
          this.importing = false
        }
      }, IMPORT_POLL_MS)
    },
    imageSrc(item) {
      return resolveMediaUrl(item.local_img_url || item.img_url)
    },
    async runImageTask(kind) {
      this.imageTask = kind
      try {
        const start = kind === 'urls' ? fetchAdminStockPlanImageUrls : downloadAdminStockPlanImages
        const { task_id: taskId } = await start(this.planId, this.imagesOverwrite)
        this.notify('info', kind === 'urls' ? "Recherche des URLs d'images lancée." : 'Téléchargement des images lancé.')
        this.pollImages(taskId, 0)
      } catch (e) {
        this.notify('error', apiErrorMessage(e))
        this.imageTask = null
      }
    },
    // Refresh counts while the Celery task runs; stop once it reaches a final state.
    pollImages(taskId, attempt) {
      clearTimeout(this.imageTimer)
      this.imageTimer = setTimeout(async () => {
        try {
          const [plan, tasks] = await Promise.all([fetchAdminStockPlan(this.planId), fetchAdminTasks()])
          this.plan = plan
          const task = tasks.find(t => t.task_id === taskId)
          const done = task && TASK_DONE.includes(task.status)
          if (!done && attempt < IMAGE_POLL_MAX) {
            this.pollImages(taskId, attempt + 1)
            return
          }
          this.imageTask = null
          this.loadItems()
          if (task && task.status === 'FAILURE') this.notify('error', "La tâche d'images a échoué : voir l'onglet Tâches.")
          else if (task && task.result && task.result.aborted) {
            this.notify('warning', 'Recherche interrompue (probable limitation de débit) : relancez plus tard pour continuer.')
          } else this.notify('success', 'Tâche terminée.')
        } catch (e) {
          this.imageTask = null
        }
      }, IMAGE_POLL_MS)
    },
    debouncedLoadItems() {
      clearTimeout(this.itemsTimer)
      this.itemsTimer = setTimeout(this.loadItems, 300)
    },
    async loadItems() {
      this.itemsLoading = true
      try {
        const data = await fetchAdminStockPlanItems(this.planId, {
          q: this.itemsQuery || '',
          status: this.itemsStatus,
          limit: 500,
        })
        this.items = data.results
        this.itemsCount = data.count
      } catch (e) {
        this.items = []
      } finally {
        this.itemsLoading = false
      }
    },
    async remove() {
      if (!window.confirm(`Supprimer le plan « ${this.plan.name} » et tous ses articles ?`)) return
      try {
        await deleteAdminStockPlan(this.planId)
        this.$router.push({ name: 'admin', query: { tab: 'plans-stock' } })
      } catch (e) {
        this.notify('error', apiErrorMessage(e))
      }
    },
    formatAttributes(attrs) {
      return Object.entries(attrs || {})
        .map(([k, v]) => `${k}=${Array.isArray(v) ? v.join('/') : v}`)
        .join(' · ')
    },
    statusLabel(status) {
      return (STOCK_PLAN_STATUS[status] || {}).label || status
    },
    statusColor(status) {
      return (STOCK_PLAN_STATUS[status] || {}).color || 'grey'
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleString('fr-FR') : '—'
    },
  },
}
</script>

<style scoped>
.mapping-editor ::v-deep textarea {
  font-family: monospace;
  font-size: 12px;
}
</style>
